import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getSchedulePerformance, getBillingPerformance } from '../api';
import { useTheme } from '../hooks/useTheme';

const C = {
  blue600: '#2563eb', green600: '#16a34a', green50: '#f0fdf4',
  rose600: '#dc2626', rose50: '#fef2f2', amber600: '#d97706',
  indigo: '#6366f1',
};
const $p = (v) => parseFloat(v || 0).toFixed(1) + '%';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '–';

const SchedulePerformanceTab = ({ projectId }) => {
  const t = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | behind | ahead | ontrack
  const [sortBy, setSortBy] = useState('variance'); // variance | code | name | pct

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const d = await getSchedulePerformance(projectId);
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const card = { background: t.bg, borderRadius: 16, border: `1px solid ${t.cardBorder}`, padding: '24px 28px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,.03)' };

  if (loading) return (
    <div style={{ padding: '80px 20px', textAlign: 'center' }}>
      <div style={{ width: 44, height: 44, border: `3px solid ${t.dark ? 'rgba(255,255,255,.1)' : '#f1f5f9'}`, borderTopColor: C.indigo, borderRadius: '50%', margin: '0 auto 16px', animation: 'spin .7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ color: t.textSecondary, fontSize: 14, fontWeight: 500 }}>Loading schedule performance...</div>
    </div>
  );

  if (!data || !data.has_baseline) return (
    <div style={{ ...card, textAlign: 'center', padding: '70px 20px' }}>
      <div style={{ fontSize: 52, marginBottom: 16, opacity: .3 }}>📊</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: t.textSecondary, marginBottom: 8 }}>No Baseline Snapshot</div>
      <div style={{ fontSize: 13, color: t.textSecondary, maxWidth: 400, margin: '0 auto', lineHeight: 1.7 }}>
        A baseline schedule snapshot is created when you generate your first billing period. Create a pay application in the Billing tab to establish the baseline, then come back here to track variance.
      </div>
    </div>
  );

  const { comparison, summary } = data;

  // Filter
  const filtered = comparison.filter(row => {
    if (filter === 'behind') return row.finish_variance_days > 0;
    if (filter === 'ahead') return row.finish_variance_days < 0;
    if (filter === 'ontrack') return row.finish_variance_days === 0 || row.finish_variance_days === null;
    return true;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'variance') return (b.finish_variance_days || 0) - (a.finish_variance_days || 0);
    if (sortBy === 'code') return (a.task_code || '').localeCompare(b.task_code || '');
    if (sortBy === 'name') return (a.task_name || '').localeCompare(b.task_name || '');
    if (sortBy === 'pct') return (b.current_pct || 0) - (a.current_pct || 0);
    return 0;
  });

  const pill = (key, active, color) => ({
    padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, transition: 'all .15s',
    background: active ? (color || C.indigo) : 'transparent',
    color: active ? '#fff' : t.textSecondary,
    boxShadow: active ? '0 2px 8px rgba(0,0,0,.12)' : 'none',
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: t.heading, letterSpacing: '-.4px' }}>Schedule Performance</h2>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: t.textSecondary }}>
            Baseline vs current schedule — {comparison.length} tasks tracked
          </p>
        </div>
        <button onClick={fetchData} style={{
          padding: '8px 18px', fontSize: 13, fontWeight: 600, borderRadius: 10,
          border: `1px solid ${t.cardBorder}`, background: t.bgAlt, color: t.heading, cursor: 'pointer',
        }}>Refresh</button>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Behind Schedule', value: summary.behind, color: C.rose600, bg: t.dark ? 'rgba(220,38,38,.1)' : C.rose50 },
          { label: 'Ahead of Schedule', value: summary.ahead, color: C.green600, bg: t.dark ? 'rgba(22,163,74,.1)' : C.green50 },
          { label: 'On Track', value: summary.onTrack, color: t.textSecondary, bg: t.bgAlt },
          { label: 'Avg. Variance', value: `${summary.avgVariance > 0 ? '+' : ''}${summary.avgVariance}d`, color: summary.avgVariance > 0 ? C.rose600 : summary.avgVariance < 0 ? C.green600 : t.heading, bg: t.bgAlt },
        ].map((kpi, i) => (
          <div key={i} style={{ ...card, padding: '18px 22px', marginBottom: 0, backgroundColor: kpi.bg }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8, color: t.textSecondary }}>{kpi.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: kpi.color, marginTop: 6 }}>{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Filter + Sort */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 4, background: t.bgAlt, borderRadius: 24, padding: 3, border: `1px solid ${t.cardBorder}` }}>
          <button onClick={() => setFilter('all')} style={pill('all', filter === 'all')}>All ({comparison.length})</button>
          <button onClick={() => setFilter('behind')} style={pill('behind', filter === 'behind', C.rose600)}>Behind ({summary.behind})</button>
          <button onClick={() => setFilter('ahead')} style={pill('ahead', filter === 'ahead', C.green600)}>Ahead ({summary.ahead})</button>
          <button onClick={() => setFilter('ontrack')} style={pill('ontrack', filter === 'ontrack')}>On Track ({summary.onTrack})</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: t.textSecondary, fontWeight: 600 }}>Sort:</span>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{
            padding: '5px 10px', fontSize: 12, border: `1px solid ${t.cardBorder}`, borderRadius: 8,
            background: t.bg, color: t.heading, cursor: 'pointer',
          }}>
            <option value="variance">Variance (worst first)</option>
            <option value="code">Task Code</option>
            <option value="name">Task Name</option>
            <option value="pct">% Complete</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          {/* Header */}
          <div style={{
            display: 'grid', gridTemplateColumns: '70px 1fr 90px 90px 70px 90px 90px 70px 100px',
            padding: '12px 16px', backgroundColor: t.bgAlt, borderBottom: `2px solid ${t.tableBorder}`,
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: t.textSecondary, gap: 8, minWidth: 900,
          }}>
            <div>Code</div><div>Task</div>
            <div style={{ textAlign: 'center' }}>Base Start</div>
            <div style={{ textAlign: 'center' }}>Base Finish</div>
            <div style={{ textAlign: 'right' }}>Base %</div>
            <div style={{ textAlign: 'center' }}>Curr Start</div>
            <div style={{ textAlign: 'center' }}>Curr Finish</div>
            <div style={{ textAlign: 'right' }}>Curr %</div>
            <div style={{ textAlign: 'center' }}>Variance</div>
          </div>

          {/* Rows */}
          {sorted.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: t.textSecondary, fontSize: 14 }}>No tasks match the current filter.</div>
          ) : sorted.map((row, idx) => {
            const fv = row.finish_variance_days;
            const isBehind = fv > 0;
            const isAhead = fv < 0;
            const rowBg = isBehind
              ? (t.dark ? 'rgba(220,38,38,.06)' : '#fff5f5')
              : isAhead
                ? (t.dark ? 'rgba(22,163,74,.06)' : '#f0fdf4')
                : 'transparent';

            return (
              <div key={row.task_id} style={{
                display: 'grid', gridTemplateColumns: '70px 1fr 90px 90px 70px 90px 90px 70px 100px',
                padding: '10px 16px',
                borderBottom: idx < sorted.length - 1 ? `1px solid ${t.tableBorder}` : 'none',
                alignItems: 'center', gap: 8, minWidth: 900, fontSize: 13,
                backgroundColor: rowBg,
              }}>
                <div style={{ fontWeight: 600, color: C.blue600, fontSize: 12 }}>{row.task_code || '–'}</div>
                <div style={{ color: t.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.task_name}>{row.task_name}</div>
                <div style={{ textAlign: 'center', color: t.textSecondary, fontSize: 12 }}>{fmtDate(row.baseline_start)}</div>
                <div style={{ textAlign: 'center', color: t.textSecondary, fontSize: 12 }}>{fmtDate(row.baseline_finish)}</div>
                <div style={{ textAlign: 'right', color: t.textSecondary, fontSize: 12 }}>{row.baseline_pct !== null ? $p(row.baseline_pct) : '–'}</div>
                <div style={{ textAlign: 'center', color: t.heading, fontWeight: 500, fontSize: 12 }}>{fmtDate(row.current_start)}</div>
                <div style={{ textAlign: 'center', color: isBehind ? C.rose600 : isAhead ? C.green600 : t.heading, fontWeight: 500, fontSize: 12 }}>{fmtDate(row.current_finish)}</div>
                <div style={{ textAlign: 'right', color: t.heading, fontWeight: 500, fontSize: 12 }}>{row.current_pct !== null ? $p(row.current_pct) : '–'}</div>
                <div style={{ textAlign: 'center' }}>
                  {fv !== null ? (
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                      backgroundColor: isBehind ? (t.dark ? 'rgba(220,38,38,.15)' : C.rose50) : isAhead ? (t.dark ? 'rgba(22,163,74,.15)' : C.green50) : t.bgAlt,
                      color: isBehind ? C.rose600 : isAhead ? C.green600 : t.textSecondary,
                    }}>
                      {isBehind ? `+${fv}d late` : isAhead ? `${fv}d early` : 'On time'}
                    </span>
                  ) : <span style={{ color: t.textSecondary, fontSize: 12 }}>–</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   BILLING PERFORMANCE SECTION
   ═══════════════════════════════════════════════ */
const $f = (v) => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',maximumFractionDigits:0}).format(+v||0);
const $s = (v) => { const n=+v||0; if(n>=1e6) return `$${(n/1e6).toFixed(1)}M`; if(n>=1e3) return `$${(n/1e3).toFixed(0)}K`; return `$${n.toFixed(0)}`; };
const fmtMonth = (ym) => { if(!ym) return ''; const [y,m]=ym.split('-'); return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][+m-1]} '${y.slice(2)}`; };

const useContainerWidth = (ref, fallback = 700) => {
  const [w, setW] = useState(fallback);
  useEffect(() => {
    if (!ref.current) return;
    const measure = () => { const cw = ref.current?.clientWidth; if (cw && cw > 100) setW(cw); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return w;
};

const BillingVarianceChart = ({ months, t }) => {
  const ref = useRef(null);
  const [tip, setTip] = useState(null);
  const [hI, setHI] = useState(-1);
  const cW = useContainerWidth(ref);

  const P = {t:28,r:16,b:48,l:62};
  const H = 280, W = Math.max(months.length * 72, cW);
  const iW=W-P.l-P.r, iH=H-P.t-P.b;
  const gap = iW / Math.max(months.length, 1);
  const bW = Math.min(20, gap * .32);
  const maxVal = Math.max(...months.map(m => Math.max(m.projected, m.actual)), 1) * 1.15;

  const show = (e, m) => {
    const rc = ref.current.getBoundingClientRect();
    const v = m.variance;
    setTip({ x:e.clientX-rc.left, y:e.clientY-rc.top, month:fmtMonth(m.month),
      projected: $f(m.projected), actual: $f(m.actual),
      retainage: m.retainageHeld > 0 ? $f(m.retainageHeld) : null,
      variance: `${v>=0?'+':''}${$f(v)}`, varColor: v>=0?C.green600:C.rose600 });
  };

  return (
    <div ref={ref} style={{ position:'relative', overflowX:'auto', overflowY:'visible' }}>
      {tip && (
        <div style={{
          position:'absolute', left:tip.x, top:tip.y, transform:'translate(-50%,-120%)',
          pointerEvents:'none', zIndex:30, background:'#0f172a', color:'#fff',
          padding:'10px 16px', borderRadius:12, fontSize:12, fontWeight:600,
          boxShadow:'0 12px 32px rgba(0,0,0,.2)', whiteSpace:'nowrap',
        }}>
          <div style={{ fontSize:11, color:'#94a3b8', marginBottom:6, fontWeight:700, textTransform:'uppercase', letterSpacing:.5 }}>{tip.month}</div>
          <div style={{ display:'flex', gap:12, marginBottom:4 }}>
            <span style={{ color:'#94a3b8', minWidth:70 }}>Projected</span>
            <span style={{ fontWeight:700 }}>{tip.projected}</span>
          </div>
          <div style={{ display:'flex', gap:12, marginBottom:4 }}>
            <span style={{ color:'#94a3b8', minWidth:70 }}>Actual</span>
            <span style={{ fontWeight:700 }}>{tip.actual}</span>
          </div>
          {tip.retainage && (
            <div style={{ display:'flex', gap:12, marginBottom:4 }}>
              <span style={{ color:'#94a3b8', minWidth:70 }}>Retainage</span>
              <span style={{ fontWeight:600, color:'#d97706' }}>+{tip.retainage}</span>
            </div>
          )}
          <div style={{ display:'flex', gap:12, borderTop:'1px solid rgba(255,255,255,.1)', paddingTop:4, marginTop:2 }}>
            <span style={{ color:'#94a3b8', minWidth:70 }}>Variance</span>
            <span style={{ fontWeight:700, color:tip.varColor }}>{tip.variance}</span>
          </div>
          <div style={{ position:'absolute', bottom:-6, left:'50%', transform:'translateX(-50%)', width:0, height:0,
            borderLeft:'7px solid transparent', borderRight:'7px solid transparent', borderTop:'7px solid #0f172a' }} />
        </div>
      )}
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMinYMid meet" style={{ display:'block', minWidth:months.length>8?W:'auto' }}>
        <defs>
          <linearGradient id="bpProj" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.indigo} stopOpacity=".9" />
            <stop offset="100%" stopColor="#a5b4fc" stopOpacity=".7" />
          </linearGradient>
          <linearGradient id="bpAct" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.green600} />
            <stop offset="100%" stopColor="#6ee7b7" />
          </linearGradient>
        </defs>

        {/* Grid */}
        {Array.from({length:6},(_,i)=>{
          const y=P.t+iH-(i/5)*iH;
          const gridColor = t.dark ? 'rgba(255,255,255,.06)' : '#f1f5f9';
          const borderColor = t.dark ? 'rgba(255,255,255,.12)' : '#e2e8f0';
          return <g key={i}>
            <line x1={P.l} x2={W-P.r} y1={y} y2={y} stroke={i===0?borderColor:gridColor} />
            <text x={P.l-10} y={y+4} textAnchor="end" fill={t.textSecondary} fontSize={10} fontFamily="system-ui">{$s((i/5)*maxVal)}</text>
          </g>;
        })}

        {/* Bars per month */}
        {months.map((m,i) => {
          const cx = P.l + i*gap + gap/2;
          const pH = (m.projected/maxVal)*iH;
          const aH = (m.actual/maxVal)*iH;
          const baseY = P.t+iH;
          const isH = hI===i;

          return <g key={m.month}
            onMouseMove={e=>{setHI(i);show(e,m);}}
            onMouseLeave={()=>{setHI(-1);setTip(null);}}
            style={{cursor:'pointer'}}
          >
            <rect x={cx-gap/2} y={P.t} width={gap} height={iH}
              fill={isH?'rgba(99,102,241,.04)':'transparent'} />

            {/* Projected bar */}
            <rect x={cx-bW-2} y={baseY-pH} width={bW} height={Math.max(pH,0)}
              rx={4} fill="url(#bpProj)" opacity={isH?1:.8} />

            {/* Actual bar */}
            {m.actual>0 && <rect x={cx+2} y={baseY-aH} width={bW} height={Math.max(aH,0)}
              rx={4} fill="url(#bpAct)" opacity={isH?1:.8} />}

            {/* Month label */}
            <text x={cx} y={baseY+20} textAnchor="middle"
              fill={isH?t.heading:t.textSecondary} fontSize={10} fontWeight={isH?700:400} fontFamily="system-ui">
              {fmtMonth(m.month)}
            </text>
          </g>;
        })}
      </svg>
    </div>
  );
};

const BillingPerformanceSection = ({ projectId }) => {
  const t = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const card = { background: t.bg, borderRadius: 16, border: `1px solid ${t.cardBorder}`, padding: '24px 28px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,.03)' };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const d = await getBillingPerformance(projectId);
        setData(d);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [projectId]);

  if (loading) return (
    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
      <div style={{ color: t.textSecondary, fontSize: 13 }}>Loading billing performance...</div>
    </div>
  );

  if (!data || !data.has_baseline) return (
    <div style={{ ...card, textAlign: 'center', padding: '50px 20px' }}>
      <div style={{ fontSize: 40, marginBottom: 12, opacity: .3 }}>💰</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: t.textSecondary, marginBottom: 6 }}>No Billing Baseline</div>
      <div style={{ fontSize: 13, color: t.textSecondary, maxWidth: 400, margin: '0 auto', lineHeight: 1.7 }}>
        The billing baseline is established from your first billing period's schedule snapshot. Create and finalize a pay application to start tracking billing performance.
      </div>
    </div>
  );

  const { months, summary } = data;
  if (!months || months.length === 0) return null;

  const varColor = summary.totalVariance >= 0 ? C.green600 : C.rose600;
  const varBg = summary.totalVariance >= 0
    ? (t.dark ? 'rgba(22,163,74,.1)' : '#f0fdf4')
    : (t.dark ? 'rgba(220,38,38,.1)' : '#fef2f2');

  return (
    <div>
      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={{ ...card, padding: '18px 22px', marginBottom: 0, background: t.dark ? 'rgba(99,102,241,.08)' : 'rgba(99,102,241,.04)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8, color: t.textSecondary }}>Original Projected</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.indigo, marginTop: 6 }}>{$f(summary.totalProjected)}</div>
        </div>
        <div style={{ ...card, padding: '18px 22px', marginBottom: 0, background: t.dark ? 'rgba(22,163,74,.08)' : 'rgba(22,163,74,.04)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8, color: t.textSecondary }}>Actual Billed</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.green600, marginTop: 6 }}>{$f(summary.totalActual)}</div>
          {summary.totalRetainage > 0 && (
            <div style={{ fontSize: 11, color: '#d97706', marginTop: 3 }}>
              (+{$f(summary.totalRetainage)} retainage held)
            </div>
          )}
        </div>
        <div style={{ ...card, padding: '18px 22px', marginBottom: 0, background: varBg }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8, color: t.textSecondary }}>Total Variance</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: varColor, marginTop: 6 }}>
            {summary.totalVariance >= 0 ? '+' : ''}{$f(summary.totalVariance)}
          </div>
          <div style={{ fontSize: 11, color: t.textSecondary, marginTop: 2 }}>
            {summary.variancePct >= 0 ? '+' : ''}{summary.variancePct}%
          </div>
        </div>
        <div style={{ ...card, padding: '18px 22px', marginBottom: 0, background: t.bgAlt }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8, color: t.textSecondary }}>Billing Accuracy</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: t.heading, marginTop: 6 }}>
            {summary.totalProjected > 0 ? Math.max(0, Math.round((1 - Math.abs(summary.totalVariance) / summary.totalProjected) * 100)) : 0}%
          </div>
          <div style={{ fontSize: 11, color: t.textSecondary, marginTop: 2 }}>
            {summary.monthsOverBudget} over, {summary.monthsUnderBudget} under
          </div>
        </div>
      </div>

      {/* Chart */}
      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:t.heading }}>Monthly: Projected vs Actual</h3>
          <div style={{ display:'flex', gap:16, alignItems:'center' }}>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <div style={{ width:10, height:10, borderRadius:3, background:C.indigo }} />
              <span style={{ fontSize:11, fontWeight:600, color:t.textSecondary }}>Projected</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <div style={{ width:10, height:10, borderRadius:3, background:C.green600 }} />
              <span style={{ fontSize:11, fontWeight:600, color:t.textSecondary }}>Actual</span>
            </div>
          </div>
        </div>
        <BillingVarianceChart months={months} t={t} />
      </div>

      {/* Month-by-month table */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '100px 1fr 1fr 1fr 1fr 1fr 1fr',
            padding: '12px 16px', backgroundColor: t.bgAlt, borderBottom: `2px solid ${t.tableBorder}`,
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: t.textSecondary, gap: 8,
          }}>
            <div>Month</div>
            <div style={{ textAlign:'right' }}>Projected</div>
            <div style={{ textAlign:'right' }}>Actual</div>
            <div style={{ textAlign:'right' }}>Variance</div>
            <div style={{ textAlign:'right' }}>Projected</div>
            <div style={{ textAlign:'right' }}>Actual</div>
            <div style={{ textAlign:'right' }}>Variance</div>
          </div>
          {months.map((m, idx) => {
            const vColor = m.variance >= 0 ? C.green600 : C.rose600;
            const cvColor = m.cumVariance >= 0 ? C.green600 : C.rose600;
            const rowBg = m.variance > 0
              ? (t.dark ? 'rgba(22,163,74,.04)' : '#f9fefb')
              : m.variance < 0
                ? (t.dark ? 'rgba(220,38,38,.04)' : '#fffbfb')
                : 'transparent';
            return (
              <div key={m.month} style={{
                display: 'grid', gridTemplateColumns: '100px 1fr 1fr 1fr 1fr 1fr 1fr',
                padding: '10px 16px', gap: 8, fontSize: 13, alignItems: 'center',
                borderBottom: idx < months.length - 1 ? `1px solid ${t.tableBorder}` : 'none',
                backgroundColor: rowBg,
              }}>
                <div style={{ fontWeight: 600, color: t.heading }}>{fmtMonth(m.month)}</div>
                <div style={{ textAlign: 'right', color: t.text }}>{$f(m.projected)}</div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: t.text, fontWeight: 600 }}>{$f(m.actual)}</div>
                  {m.retainageHeld > 0 && (
                    <div style={{ fontSize: 10, color: '#d97706', marginTop: 1 }}>+{$f(m.retainageHeld)} ret.</div>
                  )}
                </div>
                <div style={{ textAlign: 'right', color: vColor, fontWeight: 600 }}>
                  {m.variance !== 0 ? `${m.variance >= 0 ? '+' : ''}${$f(m.variance)}` : '—'}
                </div>
                <div style={{ textAlign: 'right', color: t.textSecondary, fontSize: 12 }}>{$f(m.cumProjected)}</div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: t.textSecondary, fontSize: 12 }}>{$f(m.cumActual)}</div>
                  {m.cumRetainage > 0 && (
                    <div style={{ fontSize: 10, color: '#d97706', marginTop: 1 }}>+{$f(m.cumRetainage)} ret.</div>
                  )}
                </div>
                <div style={{ textAlign: 'right', color: cvColor, fontSize: 12, fontWeight: 600 }}>
                  {m.cumVariance >= 0 ? '+' : ''}{$f(m.cumVariance)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SchedulePerformanceTab;
export { BillingPerformanceSection };
